import { Box, TableCell } from "@mui/material";
import { useState } from "react";
import { PaginatedListUser, User } from "../../../services/swagger";
import { UserService } from "../../../services/api";
import CreateUserDialog from "../../organisms/CreateUserDialog";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import DataView, { QueryParameters } from "../../organisms/DataView";
import ItemCard from "../../organisms/ItemCard";
import UserRoleChip from "../../atoms/UserRoleChip";
import { useQuery } from "@tanstack/react-query";

export default function UsersPage() {
  const { t } = useTranslation("User");
  const navigate = useNavigate();
  const [createDialog, setCreateDialog] = useState(false);
  const [query, setQuery] = useState<QueryParameters>();

  const loadUsers = async (): Promise<PaginatedListUser | null> => {
    if (!query) return null;
    const res = await UserService.getUserList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    else return null;
  };

  const { data: users, isPlaceholderData: isLoading } = useQuery({
    queryFn: loadUsers,
    queryKey: ["users", { query }],
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const navigateToUser = (user: User) => {
    navigate(`/m/user/${user._id}`);
  };

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  return (
    <Box height={1}>
      <CreateUserDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
      />
      <DataView
        name={t("Users")}
        loading={isLoading || !users}
        onItemClick={(item) => navigateToUser(item)}
        headers={[
          { field: "name", name: t("Name"), type: "string" },
          { field: "email", name: t("Email"), type: "string" },
          { field: "companyName", name: t("Company"), type: "string" },
          { field: "phone", name: t("Phone"), type: "string" },
          { field: "username", name: t("Username"), type: "string" },
          {
            field: "role",
            name: t("Role"),
            type: "other",
            renderHeaderCell: () => (
              <TableCell width={100} align="center">
                {t("Role")}
              </TableCell>
            ),
            renderRowCell: (row: User) => (
              <TableCell key={row._id} align="center">
                <UserRoleChip role={row.role} />
              </TableCell>
            ),
          },
        ]}
        onChange={handleDataTableChange}
        items={users?.docs || []}
        count={users?.count || 0}
        renderItem={(user) => (
          <ItemCard user={user} onClick={() => navigateToUser(user)} />
        )}
        pagination
        queryFunction={UserService.getUserListQuery}
        button={{
          text: t("NewUser"),
          onClick: () => setCreateDialog(true),
        }}
      />
    </Box>
  );
}
